import { Injectable } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { AbstractForm } from '@core/forms/form';
import { requiredWithTrimValueValidator } from '@libs/validator/required-with-trim-value.validator';
import { PATTERN_EMAIL } from '@libs/parameters/patterns';
import { B2BVisOrgListResponse } from '@api/models/B2bService/Contract/B2bPartner/b-2-b-vis-org-list-response';
import { B2BPartnerAddRequest } from '@api/models/B2bService/Contract/B2bPartner/b-2-b-partner-add-request';
import { isNumber } from 'lodash-es';


export enum PartnerFormField {
    OrgId = 'orgId',
    Name = 'name',
    Address = 'address',
    Phone = 'phone',
    Email = 'email',
    RegNo = 'regNo',
    AccountNum = 'accountNum',
}

export interface PartnerFormModel {
    [PartnerFormField.OrgId]: FormControl<B2BVisOrgListResponse>;
    [PartnerFormField.Name]: FormControl<string>;
    [PartnerFormField.Address]: FormControl<string>;
    [PartnerFormField.Phone]: FormControl<string>;
    [PartnerFormField.Email]: FormControl<string>;
    [PartnerFormField.RegNo]: FormControl<string>;
    [PartnerFormField.AccountNum]: FormControl<number>;
}

@Injectable()
export class PartnerFormService extends AbstractForm<PartnerFormField, PartnerFormModel> {
    public createReactiveForm(): FormGroup<PartnerFormModel> {
        this.build({
            [PartnerFormField.OrgId]: new FormControl<B2BVisOrgListResponse>(null, []),
            [PartnerFormField.Name]: new FormControl<string>(null, [requiredWithTrimValueValidator()]),
            [PartnerFormField.Address]: new FormControl<string>(null, [Validators.required]),
            [PartnerFormField.Phone]: new FormControl<string>(null, []),
            [PartnerFormField.Email]: new FormControl<string>(null, [Validators.pattern(PATTERN_EMAIL)]),
            [PartnerFormField.RegNo]: new FormControl<string>(null, []),
            [PartnerFormField.AccountNum]: new FormControl<number>(null, [Validators.required]),
        });

        this.defineMechanics();

        return this.form;
    }

    public prepareFormDataBeforeSubmit(orgId?: number): B2BPartnerAddRequest {
        const org: B2BVisOrgListResponse = this.getValueByPath(PartnerFormField.OrgId);
        const accountNum: number = Number(this.getValueByPath(PartnerFormField.AccountNum));

        return {
            ...this.form.getRawValue(),
            [PartnerFormField.AccountNum]: isNumber(accountNum) ? accountNum : null,
            [PartnerFormField.OrgId]: orgId || org?.orgId || null,
        };
    }

    public patchFormByOrg(org: B2BVisOrgListResponse): void {
        if (org) {
            this.form.patchValue({
                [PartnerFormField.Name]: org.orgName,
                [PartnerFormField.Address]: org.address,
                [PartnerFormField.Phone]: org.phone,
                [PartnerFormField.Email]: org.email,
                [PartnerFormField.RegNo]: org.regNo,
                [PartnerFormField.AccountNum]: org.accountNum,
            });
        }
    }

    private defineMechanics(): void {
        this.watchForValueChanges(PartnerFormField.OrgId).subscribe((org: B2BVisOrgListResponse) => {
            if (org) {
                this.patchFormByOrg(org);

                this.getControlByPath([PartnerFormField.Name]).disable();
                this.getControlByPath([PartnerFormField.Phone]).disable();
                this.getControlByPath([PartnerFormField.Email]).disable();
                this.getControlByPath([PartnerFormField.RegNo]).disable();
                this.getControlByPath([PartnerFormField.Address]).disable();
            } else {
                this.getControlByPath([PartnerFormField.Name]).enable();
                this.getControlByPath([PartnerFormField.Phone]).enable();
                this.getControlByPath([PartnerFormField.Email]).enable();
                this.getControlByPath([PartnerFormField.RegNo]).enable();
                this.getControlByPath([PartnerFormField.Address]).enable();
            }
        });
    }
}
